<template>
  <div class="flex flex-row gap-3">
    <div class="flex flex-col min-w-[68px] shrink-0 space-y-1">
      <Label for="login-country">
        <span
          class="text-basics-primary font-semibold opacity-90"
          :class="{
            'text-xs': variant === 'sm',
            'text-sm': variant === 'normal' || !variant,
          }"
        >
          {{ $t("channel.auth.phone.input.countryCode") }}
        </span>
      </Label>

      <CountrySelector
        id="login-country"
        v-model="countryCode"
        :readonly="readonly"
      />
    </div>

    <label for="phoneNumber" class="flex flex-col flex-1 space-y-1">
      <div class="text-basics-primary">
        <span
          class="font-semibold opacity-90"
          :class="{
            'text-xs': variant === 'sm',
            'text-sm': variant === 'normal' || !variant,
            'text-basics-accent-red': error,
          }"
          >{{ $t("channel.auth.phone.input.phoneNumber") }}</span
        >
      </div>

      <div class="relative">
        <input
          id="phoneNumber"
          ref="phoneNumberRef"
          class="w-full grow px-4 py-2 border-brand-tertiary-60 border rounded-lg bg-brand-bgdivider-10 text-basics-primary text-base"
          type="text"
          max="250"
          :readonly="readonly"
          :value="phoneNumber"
          :pattern="phonePattern"
          @input="onPhoneUpdate"
          @blur="onPhoneBlur"
          @focus="onPhoneFocus"
          @keydown.enter="onPhoneBlur"
        />
        <slot name="decoration" />
      </div>

      <p
        v-if="error"
        role="alert"
        class="text-basics-accent-red font-sans font-medium text-sm"
      >
        <Icon icon="mdi:info-outline" class="inline-block size-5 mb-[2px]" />
        {{ error }}
      </p>
    </label>
  </div>
</template>

<script setup lang="ts">
import { countries, type CountryCode } from "@/constants/country";
import CountrySelector from "./country-selector.vue";
import { useTemplateRef } from "vue";
import { useInputMask, maskArrayToFn } from "@/composables/use-input-mask";
import { useTrackingStore } from "@/stores/tracking.store";
import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import { useChannelStore } from "@/stores/channel.store";
import { Label } from "reka-ui";
import { Icon } from "@iconify/vue";

const channelStore = useChannelStore();

const countryCode = defineModel<CountryCode | undefined>("countryCode", {
  required: true,
});

const phoneNumber = defineModel<string | undefined>("phoneNumber", {
  required: true,
});

defineProps<{
  error: string | null;
  readonly?: boolean;
  variant?: "sm" | "normal";
}>();

const phoneNumberRef = useTemplateRef("phoneNumberRef");
const { onChange: onPhoneUpdate } = useInputMask(
  phoneNumberRef,
  phoneNumber,
  maskArrayToFn([/[0-9$]*/i]),
);

const phonePattern = "[0-9]{4,12}(\\$[0-9]*)?"; // 4-12 digits, optionally followed by a $ and more digits

const trackingService = useTrackingStore();
let startedEnteringPhoneNumber = false;
let phoneNumberTracked = false;

function onPhoneFocus(): void {
  if (!startedEnteringPhoneNumber) {
    startedEnteringPhoneNumber = true;
    trackingService.track({
      event: TrackEventEnum.LoginStartedEnteringInformation,
      category: EventCategory.Identity,
      metadata: {
        ...channelStore.gatingExperience,
      },
    });
  }
}

function onPhoneBlur(): void {
  const inputElement = phoneNumberRef.value;
  if (
    inputElement?.checkValidity() &&
    !phoneNumberTracked &&
    countryCode.value
  ) {
    // Track only once
    const dialCode = countries[countryCode.value].dial_code;
    trackingService.track({
      event: TrackEventEnum.LoginEnteredInformation,
      category: EventCategory.Identity,
      metadata: {
        userPhone: `+${dialCode}${phoneNumber.value}`,
        ...channelStore.gatingExperience,
      },
    });
    phoneNumberTracked = true;
  }
}

function focus() {
  const inputElement = phoneNumberRef.value;
  if (inputElement) {
    inputElement.focus();
  }
}

defineExpose({ focus });
</script>
